import React, {useCallback, useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';
import {apiErrorMessage} from '../lib/apiError';
import {ADMIN_ACTIVITY_PAGE_SIZE} from '../domain/admin';
import {
  adminResendSetupEmailApiSelector,
  adminUserDetailApiSelector,
  adminUserDetailSelector,
  adminUserEmailsPageSelector,
  adminUserTranscriptionsPageSelector,
  adminUserEmailsApiSelector,
  adminUserTranscriptionsApiSelector,
} from '../store/selectors/admin.selectors';
import {
  fetchAdminUserDetail,
  fetchAdminUserEmails,
  fetchAdminUserTranscriptions,
  isRecipientSuppressedError,
  resendSetupEmail,
} from '../store/slices/adminThunks';
import type {AdminSuppression} from '../domain/admin';
import {clearAdminUserDetail} from '../store/slices/adminSlice';
import AdminUserSummary from './admin/AdminUserSummary';
import AdminRecentConversions from './admin/AdminRecentConversions';
import AdminSetupEmailCard from './admin/AdminSetupEmailCard';
import AdminUserActivity, {AdminActivityTab} from './admin/AdminUserActivity';
import {ErrorState, LoadingState} from './admin/AdminPrimitives';
import {ArrowRightIcon, RefreshIcon} from './Icons';

/**
 * One user's page: the summary cards, the setup-email action, the recent-conversions preview, and
 * the paged activity tabs.
 *
 * The activity lists are fetched **lazily per tab** rather than both up front. An extra round trip
 * on open would be paid by every visit, and the transcription tab is the one anybody actually
 * opens — email is for when a specific question comes up. Conversions used to be a third tab here;
 * it now lives at its own route (`/admin/users/:userId/conversions`) so it can be linked to and
 * refreshed directly, the way the global transcription queue is its own page rather than a tab.
 * `AdminRecentConversions` below still shows the newest few inline, for free, since they arrive
 * with the rest of the profile in one round trip.
 *
 * The resend refetches the detail on **both** outcomes. A failed send is written to the user's
 * email timeline exactly like a successful one, so the interesting state after an error is the new
 * timeline row, not the error text on its own.
 */
const AdminUserDetailContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  const {userId} = useParams<{userId: string}>();

  const profile = useAppSelector(adminUserDetailSelector);
  const detailApi = useAppSelector(adminUserDetailApiSelector);
  const resendApi = useAppSelector(adminResendSetupEmailApiSelector);

  const transcriptions = useAppSelector(adminUserTranscriptionsPageSelector);
  const emails = useAppSelector(adminUserEmailsPageSelector);
  const transcriptionsApi = useAppSelector(adminUserTranscriptionsApiSelector);
  const emailsApi = useAppSelector(adminUserEmailsApiSelector);

  const [tab, setTab] = useState<AdminActivityTab>('transcriptions');
  const [offset, setOffset] = useState(0);

  // Two outcomes of the resend that the api status alone cannot carry: the 409's suppression
  // payload (which names the blocked address and the remedy), and the fact that a send went out
  // over a known suppression — the one "success" that has to be reported as a warning.
  const [suppressedRefusal, setSuppressedRefusal] = useState<
    {suppression: AdminSuppression; remedy: string} | null
  >(null);
  const [forcedPastSuppression, setForcedPastSuppression] = useState(false);

  // Wipe the previous user before anything renders under the new id — a detail screen showing one
  // person's name over another person's transcriptions is the kind of wrong that gets acted on.
  useEffect(() => {
    dispatch(clearAdminUserDetail());
    setTab('transcriptions');
    setOffset(0);
    setSuppressedRefusal(null);
    setForcedPastSuppression(false);
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchAdminUserDetail(userId));
    }
  }, [dispatch, userId]);

  const fetchActivity = useCallback(
    (which: AdminActivityTab, nextOffset: number) => {
      if (!userId) {
        return;
      }

      const args = {userId, limit: ADMIN_ACTIVITY_PAGE_SIZE, offset: nextOffset};

      if (which === 'transcriptions') {
        dispatch(fetchAdminUserTranscriptions(args));
      } else {
        dispatch(fetchAdminUserEmails(args));
      }
    },
    [dispatch, userId]
  );

  useEffect(() => {
    fetchActivity(tab, offset);
  }, [fetchActivity, tab, offset]);

  const handleTabChange = (next: AdminActivityTab) => {
    setTab(next);
    setOffset(0);
  };

  const handleResend = async ({force}: {force: boolean}): Promise<{suppressed: boolean}> => {
    if (!userId) {
      return {suppressed: false};
    }

    let suppressed = false;

    setSuppressedRefusal(null);
    setForcedPastSuppression(false);

    try {
      const result = await dispatch(resendSetupEmail({userId, force})).unwrap();

      // A forced send is not a success worth a green toast: Resend accepted it and almost
      // certainly dropped it, and telling an operator "sent" here is how somebody stops chasing a
      // customer who still cannot get in.
      if (result.forcedPastSuppression) {
        setForcedPastSuppression(true);
        showToast(
          `Sent to ${result.recipient} over an active suppression — it was probably not delivered`,
          'warning'
        );
      } else {
        showToast(`Setup email sent to ${result.recipient}`, 'success');
      }
    } catch (error) {
      if (isRecipientSuppressedError(error)) {
        // Not a failure to retry: the address is blocked, so the card turns this into the override
        // rather than an error the operator would click through.
        suppressed = true;
        setSuppressedRefusal({suppression: error.suppression, remedy: error.remedy});
        showToast('That address is suppressed — nothing was sent', 'warning');
      } else {
        showToast(apiErrorMessage(error, 'The setup email could not be sent'), 'error');
      }
    } finally {
      // Either way the attempt is now on their timeline, and the summary's send count has moved.
      // A 409 is the exception — it refused before sending, so nothing was written — but refetching
      // is still right there: it is how the newly-known suppression lands on the record.
      dispatch(fetchAdminUserDetail(userId));
      if (tab === 'emails') {
        fetchActivity('emails', offset);
      }
    }

    return {suppressed};
  };

  if (!userId) {
    return <ErrorState message="No user id in the URL." />;
  }

  if (!profile && detailApi.status === 'pending') {
    return <LoadingState message="Loading this user…" />;
  }

  if (!profile) {
    return (
      <ErrorState
        message={detailApi.error ?? 'This user could not be loaded.'}
        onRetry={() => dispatch(fetchAdminUserDetail(userId))}
      />
    );
  }

  const activityApi = tab === 'transcriptions' ? transcriptionsApi : emailsApi;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Link to="/admin/users" className="text-indigo-400 hover:text-indigo-300">
            Users
          </Link>
          <ArrowRightIcon className="h-3 w-3" />
          <span className="text-gray-300">{profile.user.name || profile.user.email}</span>
        </div>

        <button
          type="button"
          onClick={() => dispatch(fetchAdminUserDetail(userId))}
          disabled={detailApi.status === 'pending'}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1
           text-[11px] font-medium text-gray-300 transition-colors hover:border-gray-600
           hover:bg-gray-800 disabled:opacity-40 cursor-pointer"
        >
          <RefreshIcon className={`h-3 w-3 ${detailApi.status === 'pending' ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <AdminSetupEmailCard
        email={profile.user.email}
        passwordSet={profile.user.passwordSet}
        setupEmail={profile.setupEmail}
        signup={profile.signup}
        suppression={profile.emailSuppression}
        onResend={handleResend}
        isResending={resendApi.status === 'pending'}
        resendError={resendApi.status === 'rejected' ? resendApi.error : null}
        suppressedRefusal={suppressedRefusal}
        forcedPastSuppression={forcedPastSuppression}
      />

      <AdminUserSummary profile={profile} />

      <AdminRecentConversions userId={userId} conversions={profile.recentConversions} />

      <AdminUserActivity
        tab={tab}
        onTabChange={handleTabChange}
        offset={offset}
        onOffsetChange={setOffset}
        pageSize={ADMIN_ACTIVITY_PAGE_SIZE}
        isLoading={activityApi.status === 'pending'}
        error={activityApi.status === 'rejected' ? activityApi.error : null}
        onRetry={() => fetchActivity(tab, offset)}
        transcriptions={transcriptions}
        emails={emails}
      />
    </div>
  );
};

export default AdminUserDetailContainer;
